import React, { useEffect, useState } from 'react'
import { NoSaleReasonCodeOption } from '../../contexts/SellerQueueContext'
import { getNoSaleReasonCodes } from '../../services/sellerQueueService'
import './index.css'

export interface AttendanceModalProps {
  isOpen: boolean
  isLoading?: boolean
  onClose: () => void
  onOutcomeSelected: (outcome: string) => Promise<void>
  onNoSaleSubmit?: (data: NoSaleFormData) => Promise<void>
}

export interface NoSaleFormData {
  reasonCode: number | null
  note?: string
}

/**
 * AttendanceModal
 * Modal for completing seller service with outcome selection
 * If "no_sale" selected, expands to show form
 */
export const AttendanceModal: React.FC<AttendanceModalProps> = ({
  isOpen,
  isLoading,
  onClose,
  onOutcomeSelected,
  onNoSaleSubmit,
}) => {
  const [saleOutcome, setSaleOutcome] = useState<'sale_completed' | 'no_sale' | null>(null)
  const [noSaleForm, setNoSaleForm] = useState<NoSaleFormData>({
    reasonCode: null,
    note: '',
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [reasonOptions, setReasonOptions] = useState<NoSaleReasonCodeOption[]>([])

  const getReasonLabel = (reason: Partial<NoSaleReasonCodeOption> & Record<string, any>): string => {
    return (
      reason.saleReasonCode ||
      reason.slugSaleReasonCode ||
      reason.reasonDescription ||
      reason.description ||
      reason.reason ||
      (reason.id ? `Motivo ${reason.id}` : 'Motivo não informado')
    )
  }

  useEffect(() => {
    if (!isOpen) {
      setSaleOutcome(null)
      setNoSaleForm({ reasonCode: null, note: '' })
      setFormError(null)
      return
    }

    const loadReasonCodes = async () => {
      try {
        const data = await getNoSaleReasonCodes()
        setReasonOptions(data)
      } catch (error) {
        console.error('Erro ao carregar motivos de não venda:', error)
        setReasonOptions([])
      }
    }

    loadReasonCodes()
  }, [isOpen])

  if (!isOpen) return null

  const handleNoSaleFormChange = (field: keyof NoSaleFormData, value: string) => {
    const parsedValue = field === 'reasonCode' ? (value ? Number(value) : null) : value
    setNoSaleForm((prev) => ({ ...prev, [field]: parsedValue }))
    setFormError(null)
  }

  const handleConfirm = async () => {
    if (!saleOutcome) {
      setFormError('Selecione o resultado do atendimento')
      return
    }

    if (saleOutcome === 'no_sale' && !noSaleForm.reasonCode) {
      setFormError('Selecione um motivo')
      return
    }

    try {
      if (saleOutcome === 'no_sale' && onNoSaleSubmit) {
        await onNoSaleSubmit({ reasonCode: noSaleForm.reasonCode, note: noSaleForm.note })
      } else {
        await onOutcomeSelected('sale_completed')
      }
      setSaleOutcome(null)
      setNoSaleForm({ reasonCode: null, note: '' })
      onClose()
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Erro ao salvar')
    }
  }

  const canProceed =
    !!saleOutcome && (saleOutcome === 'sale_completed' || (saleOutcome === 'no_sale' && !!noSaleForm.reasonCode))

  return (
    <div className="attendance-modal-overlay">
      <div className="attendance-modal">
        <div className="attendance-modal-header">
          <h2>Resultado do Atendimento</h2>
          <button className="attendance-modal-close-btn" onClick={onClose} disabled={isLoading}>
            ✕
          </button>
        </div>

        <div className="attendance-modal-content">
          <div className="outcomes-step">
            <p className="step-description">Selecione a opção do atendimento:</p>

            <div className="outcome-buttons">
              <label className={`outcome-option ${saleOutcome === 'sale_completed' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="saleOutcome"
                  value="sale_completed"
                  checked={saleOutcome === 'sale_completed'}
                  onChange={() => {
                    setSaleOutcome('sale_completed')
                    setNoSaleForm({ reasonCode: null, note: '' })
                    setFormError(null)
                  }}
                  disabled={isLoading}
                />
                <span>Venda Concluída</span>
              </label>

              <label className={`outcome-option ${saleOutcome === 'no_sale' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="saleOutcome"
                  value="no_sale"
                  checked={saleOutcome === 'no_sale'}
                  onChange={() => {
                    setSaleOutcome('no_sale')
                    setFormError(null)
                  }}
                  disabled={isLoading}
                />
                <span>Venda Não Concluída</span>
              </label>
            </div>

            {saleOutcome === 'no_sale' && (
              <div className="attendance-form-group">
                <label className="attendance-form-label" htmlFor="reasonCode">
                  Motivo da Não Venda:
                </label>
                <select
                  id="reasonCode"
                  value={noSaleForm.reasonCode ?? ''}
                  onChange={(e) => handleNoSaleFormChange('reasonCode', e.target.value)}
                  disabled={isLoading}
                  className="attendance-form-input attendance-modal-select"
                >
                  <option value="">{reasonOptions.length ? 'Selecione um motivo...' : 'Nenhum motivo disponível'}</option>
                  {reasonOptions.map((reason) => (
                    <option key={reason.id} value={reason.id}>
                      {getReasonLabel(reason)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formError && <div className="form-error">{formError}</div>}
          </div>
        </div>

        <div className="attendance-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={isLoading}>
            Cancelar
          </button>

          <button className="btn btn-primary" onClick={handleConfirm} disabled={isLoading || !canProceed}>
            {isLoading ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}
